import * as React from 'react'
import { BetterComponent } from '../../../components/better-component/better-component'
import { BetterSelect } from '../../../components/better-select/better-select'
import { IFilterBuilderProps, IFilterBuilderState } from '../interfaces/filter-builder'
import * as deepEqual from 'react-fast-compare'
import { DomainService } from '../../../services'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc/es5'
import { getClassNames } from '../../../_utils/classnames'
import clone from 'clone'
import { Loading3QuartersOutlined } from '@ant-design/icons'

interface IState extends IFilterBuilderState {
    domainKeywords?: any[]
}

export class NotificationKeywordsFilterBuilder extends BetterComponent<IFilterBuilderProps, IState> {
    public state: IState = {
        operator: 'EQ',
        value: undefined,
    }

    private appState: AppState
    private domainService: DomainService

    public constructor(props: IFilterBuilderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.domainService = Container.get(DomainService)
    }

    public componentDidMount(): void {
        this.loadDomainKeywords()
    }

    public render(): React.ReactNode {
        return (
            <div className={getClassNames('filter', 'nkw-filter')}>
                <div className={getClassNames('filter-wrapper')}>
                    <div className={getClassNames('filter-display')}>
                        <span>
                            <span className="filter-op">{this.currentOperatorDisplay}</span>
                            <span> </span>
                            <span className="filter-val">{this.currentValueDisplay}</span>
                        </span>
                    </div>
                    <div className={getClassNames('filter-editor')}>
                        <div className="filter-op">
                            <BetterSelect
                                size="small"
                                disableSearch={true}
                                dropdownClassName="filter-op-dropdown"
                                options={[
                                    { value: 'EQ', label: 'contains' },
                                    { value: 'NEQ', label: 'do not contain' },
                                ]}
                                defaultValue={['EQ']}
                                value={this.currentValue.operator}
                                onChange={(operator: string) => {
                                    this.handleChange({ operator })
                                }}
                            />
                        </div>
                        <div className="filter-val">
                            <BetterSelect
                                size="small"
                                mode="multiple"
                                dropdownClassName="filter-val-dropdown"
                                placeholder={
                                    !this.state.domainKeywords ? <Loading3QuartersOutlined spin={true} /> : 'Select...'
                                }
                                loading={!this.state.domainKeywords}
                                disabled={!this.state.domainKeywords}
                                disableSelectAll={true}
                                maxDisplayCount={2}
                                maxDisplayFormatter={(options) => {
                                    return `${options.length} Keywords`
                                }}
                                options={(this.state.domainKeywords || []).map((kw) => ({ value: kw.name }))}
                                value={this.currentValue.value}
                                onChange={this.handleValueChange}
                                onClose={this.handleValueChangeComplete}
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    protected get currentValue(): any {
        const propsValue: any = this.props.value || {}
        return clone({
            field: 'NOTIFICATION_KEYWORDS',
            value: this.state.value || propsValue.value,
            operator: propsValue.operator || 'EQ',
        })
    }

    protected get currentOperatorDisplay(): string {
        const currentValue = this.currentValue.value || []

        return this.currentValue.operator === 'EQ'
            ? currentValue.length === 1
                ? 'contains'
                : 'contains one of'
            : currentValue.length === 1
            ? 'does not contain'
            : 'does not contain one of'
    }

    protected get currentValueDisplay(): string {
        let display: string = ''

        if (!this.currentValue.value) {
            display = 'any keyword'
        } else {
            const currentValue = clone(this.currentValue.value || [])
            if (currentValue.length <= 2) {
                display = currentValue.join(' or ')
            } else {
                const lastValue = currentValue.pop()
                display = currentValue.join(', ') + `, or ${lastValue}`
            }
        }

        return display
    }

    protected handleValueChange = async (value: any) => {
        if (Array.isArray(value) && value.length === 0) {
            value = undefined
        }

        await this.setState({ value })
        return value
    }

    protected handleValueChangeComplete = async (value: any) => {
        value = await this.handleValueChange(value)

        if (!deepEqual(value, (this.props.value || {}).value)) {
            this.handleChange({ value })
        }
    }

    protected handleChange(config: any): any {
        if (!!config) {
            this.props.onChange({
                field: 'NOTIFICATION_KEYWORDS',
                operator: this.currentValue.operator,
                ...this.props.value,
                ...config,
            })
        } else {
            this.props.onChange()
        }

        this.setState({ value: undefined })
    }

    protected async loadDomainKeywords(): Promise<void> {
        const keywords = await this.domainService.fetchKeywordsByDomainId(this.appState.currentDomain!.id, {
            query: { pagination: 0 },
            showLoadingScreen: false,
        })

        this.setState({ domainKeywords: keywords.data || [] })
    }
}
